import {
  UNIT_DOCUMENT_KINDS,
  type UnitDocumentKind,
} from '@sitewise/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  LoadingState,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  confirmUnitDocument,
  deleteUnitDocument,
  fetchUnitDocumentSignedUrl,
  listUnitDocuments,
  presignUnitDocument,
  type UnitDocument,
} from '../../../api/units.js';
import { ApiError } from '../../../lib/api-client.js';

import type { UnitRow } from '../../../api/buildings.js';

interface Props {
  unit: UnitRow;
}

const KIND_LABELS: Record<UnitDocumentKind, string> = {
  lease: 'Lease',
  photo: 'Photo',
  floor_plan: 'Floor plan',
  manual: 'Manual',
  inspection_report: 'Inspection report',
  permit: 'Permit',
  lead_xrf_report: 'Lead XRF report',
  lead_exemption_letter: 'Lead exemption letter',
  lead_work_evidence: 'Lead work evidence',
  other: 'Other',
};

const MAX_BYTES = 50 * 1024 * 1024;

/**
 * Per-unit document store. Files go directly to Spaces via the
 * presigned PUT URL the API hands back; we only persist metadata.
 * Same UX shape as the inspection answer photo uploader, simplified
 * for arbitrary file types and no captioning until v2.
 */
export function DocumentsTab({ unit }: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadKind, setUploadKind] = useState<UnitDocumentKind>('other');
  const [uploadingName, setUploadingName] = useState<string | null>(null);

  const docsQuery = useQuery({
    queryKey: ['unit', unit.id, 'documents'],
    queryFn: () => listUnitDocuments(unit.id),
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => deleteUnitDocument(unit.id, documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id, 'documents'] });
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive document');
    },
  });

  async function handleUpload(file: File) {
    if (file.size > MAX_BYTES) {
      toast.error('File is larger than 50 MB.');
      return;
    }
    setUploadingName(file.name);
    try {
      const presigned = await presignUnitDocument(unit.id, {
        kind: uploadKind,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        caption: null,
      });

      const uploadResponse = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: presigned.headers,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload to storage failed (${uploadResponse.status})`);
      }

      await confirmUnitDocument(unit.id, presigned.document.id);
      toast.success('Document uploaded.');
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id, 'documents'] });
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingName(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function openSigned(docId: string) {
    try {
      const signed = await fetchUnitDocumentSignedUrl(unit.id, docId);
      window.open(signed.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not download');
    }
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <label className="text-xs font-medium text-fg-muted">
            Upload as
            <select
              className="ml-2 rounded border border-border bg-surface px-2 py-1 text-sm"
              value={uploadKind}
              onChange={(e) => setUploadKind(e.target.value as UnitDocumentKind)}
            >
              {UNIT_DOCUMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUpload(file);
            }}
          />
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={Boolean(uploadingName)}
          >
            <Upload className="h-3.5 w-3.5" />
            {uploadingName ? `Uploading ${uploadingName}…` : 'Upload file'}
          </Button>
          <span className="text-xs text-fg-muted">Max 50 MB.</span>
        </CardContent>
      </Card>

      {docsQuery.isLoading ? (
        <LoadingState rows={3} />
      ) : (docsQuery.data?.documents ?? []).length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-fg-secondary">
            No documents uploaded for this unit yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y divide-border p-0">
            {(docsQuery.data?.documents ?? []).map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                onDownload={() => openSigned(doc.id)}
                onDelete={() => {
                  if (confirm(`Archive ${doc.originalFilename}?`)) {
                    deleteMutation.mutate(doc.id);
                  }
                }}
                deleting={deleteMutation.isPending}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DocumentRow({
  doc,
  onDownload,
  onDelete,
  deleting,
}: {
  doc: UnitDocument;
  onDownload: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
      <div className="flex min-w-0 items-center gap-3">
        <FileText className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
        <div className="min-w-0">
          <div className="truncate font-medium">{doc.originalFilename}</div>
          <div className="text-xs text-fg-muted">
            <Badge variant="outline" className="mr-1.5 text-xs">
              {KIND_LABELS[doc.kind] ?? doc.kind}
            </Badge>
            {formatBytes(doc.bytes)} · uploaded {new Date(doc.createdAt).toLocaleDateString()}
          </div>
          {doc.caption ? (
            <div className="truncate text-xs text-fg-secondary">{doc.caption}</div>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="icon" aria-label="Download" onClick={onDownload}>
          <Download className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Archive document"
          disabled={deleting}
          className="text-danger hover:text-danger"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
